import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { DemoStoreProvider } from "@/contexts/DemoStoreContext";

export type DemoRole = "owner" | "admin" | "agent" | "branch_manager";

const DEMO_ROLE_STORAGE_KEY = "easy_rental_demo_role";

function normalizeDemoRole(role: string | null | undefined): DemoRole {
  if (role === "admin" || role === "agent" || role === "branch_manager") {
    return role;
  }
  return "owner";
}

interface DemoContextType {
  isDemo: boolean;
  demoRole: DemoRole;
  enterDemo: (role?: DemoRole) => void;
  exitDemo: () => void;
}

const DemoContext = createContext<DemoContextType | undefined>(undefined);

export function DemoProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [isDemoFlag, setIsDemoFlag] = useState(() => localStorage.getItem("demo_mode") === "true");
  const [demoRole, setDemoRole] = useState<DemoRole>(() => normalizeDemoRole(localStorage.getItem(DEMO_ROLE_STORAGE_KEY)));

  // Safety net: if a real user is authenticated, demo mode is always false
  const isDemo = isDemoFlag && !user;

  const enterDemo = useCallback((role: DemoRole = "owner") => {
    const nextRole = normalizeDemoRole(role);
    localStorage.setItem("demo_mode", "true");
    localStorage.setItem(DEMO_ROLE_STORAGE_KEY, nextRole);
    setIsDemoFlag(true);
    setDemoRole(nextRole);
  }, []);

  const exitDemo = useCallback(() => {
    localStorage.removeItem("demo_mode");
    localStorage.removeItem(DEMO_ROLE_STORAGE_KEY);
    setIsDemoFlag(false);
    setDemoRole("owner");
  }, []);

  return (
    <DemoContext.Provider value={{ isDemo, demoRole, enterDemo, exitDemo }}>
      {isDemo ? (
        <DemoStoreProvider>{children}</DemoStoreProvider>
      ) : (
        children
      )}
    </DemoContext.Provider>
  );
}

export function useDemo() {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error("useDemo must be used within DemoProvider");
  return ctx;
}
